import React, { useState, useEffect } from 'react';
import * as childrenApi from '../api/children';
import client from '../api/client';

function AuthImg({ src, alt, className, style }) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let url;
    client.get(src, { responseType: 'blob' })
      .then(res => { url = URL.createObjectURL(res.data); setBlobUrl(url); })
      .catch(() => setBlobUrl(null));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [src]);
  if (!blobUrl) return null;
  return <img src={blobUrl} alt={alt} className={className} style={style} />;
}

const AVATAR_COLORS = [
  '#FF6B9D','#7EC8E3','#06D6A0','#9B5FE3','#FFD166',
  '#FF9F43','#54A0FF','#5F27CD','#01CBC6','#EE5A24',
];

function avatarColor(id) {
  return AVATAR_COLORS[id % AVATAR_COLORS.length];
}

export default function ChildrenPage() {
  const [children, setChildren]   = useState([]);
  const [name, setName]           = useState('');
  const [excludeFromSort, setExcludeFromSort] = useState(false);
  const [photos, setPhotos]       = useState([]);
  const [previews, setPreviews]   = useState([]);
  const [dragging, setDragging]   = useState(false);
  const [loading, setLoading]     = useState(false);
  const [fetching, setFetching]   = useState(true);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');

  const fetchChildren = async () => {
    try {
      setChildren(await childrenApi.list());
    } catch {
      // サーバー未起動時はスキップ
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => { fetchChildren(); }, []);

  const addPhotos = (files) => {
    const arr = Array.from(files);
    setPhotos(p => [...p, ...arr]);
    arr.forEach(f => {
      const reader = new FileReader();
      reader.onload = e => setPreviews(p => [...p, e.target.result]);
      reader.readAsDataURL(f);
    });
  };

  const removePhoto = (i) => {
    setPhotos(p => p.filter((_, idx) => idx !== i));
    setPreviews(p => p.filter((_, idx) => idx !== i));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim())      { setError('名前を入力してください'); return; }
    if (!photos.length)    { setError('顔写真を1枚以上選択してください'); return; }
    setLoading(true); setError(''); setSuccess('');
    try {
      const fd = new FormData();
      fd.append('name', name.trim());
      fd.append('exclude_from_sort', excludeFromSort);
      photos.forEach(p => fd.append('photos', p));
      await childrenApi.register(fd);
      setName(''); setExcludeFromSort(false); setPhotos([]); setPreviews([]);
      setSuccess(`「${name.trim()}」を登録しました`);
      setTimeout(() => setSuccess(''), 3500);
      fetchChildren();
    } catch (e) {
      setError(e.response?.data?.detail ?? e.message ?? '登録に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleExclude = async (child) => {
    try {
      await childrenApi.update(child.id, { exclude_from_sort: !child.exclude_from_sort });
      fetchChildren();
    } catch {
      setError('設定の変更に失敗しました');
    }
  };

  const handleDelete = async (child) => {
    if (!window.confirm(`「${child.name}」を削除しますか？`)) return;
    try {
      await childrenApi.remove(child.id);
      fetchChildren();
    } catch {
      setError('削除に失敗しました');
    }
  };

  return (
    <div className="page">
      <div className="page-header animate-in">
        <h1 className="page-title">顔登録</h1>
        <p className="page-subtitle">子供の名前と顔写真を登録します</p>
      </div>

      <div className="page-body two-col" style={{ alignItems: 'flex-start' }}>
        {/* ── 登録フォーム ── */}
        <div className="two-col-left animate-in-delay-1">
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">名前</label>
              <input
                className="form-input"
                type="text"
                placeholder="例：田中 太郎"
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">顔写真</label>
              <div
                className={`upload-zone${dragging ? ' drag-over' : ''}`}
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={e => { e.preventDefault(); setDragging(false); addPhotos(e.dataTransfer.files); }}
              >
                <input type="file" accept="image/*" multiple onChange={e => addPhotos(e.target.files)} />
                <div className="upload-zone-icon">↑</div>
                <div className="upload-zone-text">クリックまたはドラッグ＆ドロップ</div>
                <div className="upload-zone-hint">JPG / PNG — 複数枚可</div>
              </div>

              {previews.length > 0 && (
                <div className="photo-previews">
                  {previews.map((src, i) => (
                    <div key={i} className="photo-preview">
                      <img src={src} alt="" />
                      <button type="button" className="photo-preview-remove" onClick={() => removePhoto(i)}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label
                style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={excludeFromSort}
                  onChange={e => setExcludeFromSort(e.target.checked)}
                  style={{ marginTop: 3, width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }}
                />
                <span>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>振り分け不要にする</span>
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.5 }}>
                    肖像権NG・承諾なし等。この人物が写っている写真は振り分け対象から除外されます。
                  </span>
                </span>
              </label>
            </div>

            {error   && <div className="msg msg-error">{error}</div>}
            {success && <div className="msg msg-success">{success}</div>}

            <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%', padding: '10px' }}>
              {loading ? <><span className="spinner" />登録中…</> : '登録する'}
            </button>
          </form>
        </div>

        {/* ── 縦区切り ── */}
        <div className="two-col-divider" />

        {/* ── 登録済み一覧 ── */}
        <div className="two-col-right animate-in-delay-2">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 15, fontWeight: 600 }}>登録済みの子供</h2>
            <span style={{ fontSize: 10, color: 'var(--text-muted)', background: 'var(--border-light)', padding: '2px 10px', borderRadius: 20, border: '1px solid var(--border)' }}>
              {children.length} 人
            </span>
          </div>

          {fetching ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <span className="spinner spinner-dark" style={{ width: 22, height: 22 }} />
            </div>
          ) : children.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">◉</div>
              <div className="empty-text">まだ登録された子供はいません</div>
            </div>
          ) : (
            <div className="children-grid">
              {children.map((child, i) => (
                <div key={child.id} className="child-card animate-in" style={{ animationDelay: `${i * 0.04}s` }}>
                  {child.photo_path ? (
                    <AuthImg
                      src={`/children/${child.id}/photo`}
                      alt={child.name}
                      className="child-avatar"
                      style={{ objectFit: 'cover', padding: 0 }}
                    />
                  ) : (
                    <div className="child-avatar" style={{ background: avatarColor(child.id) }}>
                      {child.name[0]}
                    </div>
                  )}
                  <div className="child-name">{child.name}</div>
                  <div className="child-id">ID: {child.id}</div>
                  {child.exclude_from_sort && (
                    <span style={{
                      display: 'inline-block', marginTop: 4, fontSize: 10, fontWeight: 700,
                      color: '#c53030', background: '#fff5f5', border: '1px solid #fed7d7',
                      borderRadius: 20, padding: '2px 8px',
                    }}>
                      振り分け不要
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => handleToggleExclude(child)}
                    style={{
                      display: 'block', marginTop: 8, fontSize: 11, color: 'var(--text-muted)',
                      background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline',
                    }}
                  >
                    {child.exclude_from_sort ? '振り分け対象に戻す' : '振り分け不要にする'}
                  </button>
                  <button className="btn btn-danger child-card-delete" onClick={() => handleDelete(child)}>
                    削除
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
